/** Builder section 6 — added value (bonus impressions at no charge) and how impressions are rounded. */

import React from 'react';
import { Switch } from '../../../ui';
import type { MediaPlanUnitDef, RateCardAddedValuePolicy } from '../types';
import { fieldId, type PlanDraft } from '../planDraft';
import { fieldStyle, gridStyle, hintStyle, labelStyle, numberFieldStyle } from '../formStyles';
import FormSection from './FormSection';
import { FieldError, fieldAria, type ErrorFor } from './fields';

interface Props {
  draft: PlanDraft;
  update: (patch: Partial<PlanDraft>) => void;
  units: MediaPlanUnitDef[];
  /** The card's bonus policy, once a quote has said which card applies. */
  policy?: RateCardAddedValuePolicy | null;
  errorFor: ErrorFor;
  disabled?: boolean;
}

/** "10% on :15 NS" */
export function policyWords(policy: RateCardAddedValuePolicy, units: readonly MediaPlanUnitDef[]): string {
  const on = policy.bonusUnits.map((k) => units.find((u) => u.key === k)?.shortLabel || k).join(', ');
  return `${Math.round(policy.bonusPct * 100)}%${on ? ` on ${on}` : ''}`;
}

const AddedValueSection: React.FC<Props> = ({ draft, update, units, policy, errorFor, disabled = false }) => {
  const pctError = errorFor('addedValuePct');
  const policyText = policy ? policyWords(policy, units) : null;
  return (
    <FormSection id="mp-section-added-value" title="Added value">
      <Switch
        checked={draft.includeAddedValue}
        onChange={(includeAddedValue) => update({ includeAddedValue })}
        label="Bonus impressions"
        description={policyText ? `Free media on top of the paid plan. The rate card gives ${policyText}.` : 'Free media on top of the paid plan, per the rate card.'}
        disabled={disabled}
      />
      <div style={gridStyle}>
        {draft.includeAddedValue && (
          <div>
            <label htmlFor={fieldId('addedValuePct')} style={labelStyle}>
              Bonus
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id={fieldId('addedValuePct')}
                type="text"
                inputMode="decimal"
                style={{ ...numberFieldStyle, paddingRight: 26 }}
                value={draft.addedValuePct}
                placeholder={policy ? String(Math.round(policy.bonusPct * 100)) : 'card'}
                disabled={disabled}
                onChange={(e) => update({ addedValuePct: e.target.value })}
                {...fieldAria('addedValuePct', pctError)}
              />
              <span style={styles.pct}>%</span>
            </div>
            <FieldError field="addedValuePct" message={pctError} />
            <div style={hintStyle}>Blank = the rate card's policy.</div>
          </div>
        )}
        <div>
          <label htmlFor={fieldId('rounding')} style={labelStyle}>
            Impressions
          </label>
          <select id={fieldId('rounding')} style={{ ...fieldStyle, cursor: 'pointer' }} value={String(draft.roundImpressionsTo)} disabled={disabled} onChange={(e) => update({ roundImpressionsTo: e.target.value === '1000' ? 1000 : 1 })}>
            <option value="1">Exact investment (impressions to the unit)</option>
            <option value="1000">Whole thousands of impressions (investment carries cents)</option>
          </select>
        </div>
      </div>
    </FormSection>
  );
};

const styles: Record<string, React.CSSProperties> = {
  pct: { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: 'var(--fg-4)', pointerEvents: 'none' },
};

export default AddedValueSection;
